import {
  Alert,
  Badge,
  Button,
  Card,
  Flex,
  Grid,
  Group,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import { notify } from '@/lib/notify';
import {
  IconCalendarEvent,
  IconChecklist,
  IconExternalLink,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMedplum } from '@medplum/react-hooks';
import { useAppointmentsData } from '../../features/appointments';
import { Loading } from '../../components/Loading';
import { JoinVideoSessionButton } from '../../components/JoinVideoSessionButton';
import {
  CHIME_JOIN_OPENS_MINUTES,
  chimeJoinNotYetOpen,
  telehealthSessionHasFinished,
} from '@/lib/telehealth/joinWindow';
import { ClientPortalLayout } from './components/ClientPortalLayout';
import type { PortalAppointment } from './data/mockClientPortalData';
import { useClientPortalTasks } from './hooks/useClientPortalTasks';
import { useEffectivePortalPatientId } from '../../hooks/usePortalSubjectPatientId';
import type { ClientPortalTaskItem } from './utils/clientPortalFhirTypes';
import { submitPortalAppointmentCancellation } from './utils/portalAppointmentCancel';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US');
  } catch {
    return iso;
  }
}

function formatTimeRange(startIso: string, endIso: string): string {
  try {
    const start = new Date(startIso);
    const end = new Date(endIso);
    const fmt = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${fmt.format(start)} – ${fmt.format(end)}`;
  } catch {
    return `${startIso} – ${endIso}`;
  }
}

function minutesUntil(iso: string): number {
  return Math.round((new Date(iso).getTime() - Date.now()) / 60_000);
}

function hoursUntil(iso: string): number {
  return minutesUntil(iso) / 60;
}

function isTelehealthVisit(apt: PortalAppointment): boolean {
  return apt.modality === 'Telehealth' || apt.locationLabel === 'Telehealth';
}

function taskBadgeColor(item: ClientPortalTaskItem): string {
  if (item.isClinicalAssessment) return 'orange';
  if (item.actionType === 'billing') return 'yellow';
  if (item.actionType === 'message') return 'blue';
  if (item.actionType === 'signature') return 'violet';
  return 'gray';
}

function taskBadgeLabel(item: ClientPortalTaskItem): string | undefined {
  if (item.isClinicalAssessment) return 'Assessment';
  if (item.due) {
    const h = hoursUntil(item.due);
    if (h < 48 && h > 0) return 'Due soon';
  }
  return undefined;
}

function upcomingAppointmentBadge(
  apt: PortalAppointment,
): { label: string; color: string } {
  switch (apt.status) {
    case 'Requested':
      return { label: 'Requested', color: 'yellow' };
    case 'Completed':
      return { label: 'Completed', color: 'green' };
    case 'Cancelled':
      return { label: 'Cancelled', color: 'gray' };
    default:
      return { label: 'Scheduled', color: 'blue' };
  }
}

/** Matched dashboard column height; inner lists scroll when content overflows. */
const DASHBOARD_PANEL_HEIGHT = 600;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ClientPortalDashboardPage(): JSX.Element {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const portalPatientId = useEffectivePortalPatientId();
  const { data: appointmentsData, loading: appointmentsLoading } = useAppointmentsData();
  const { tasks, loading: tasksLoading, error: tasksError } = useClientPortalTasks();
  const [localCancellations, setLocalCancellations] = useState<Set<string>>(() => new Set());
  const [cancelTarget, setCancelTarget] = useState<PortalAppointment | null>(null);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  const upcomingList = useMemo(() => appointmentsData?.upcoming ?? [], [appointmentsData]);
  const pastList = useMemo(() => appointmentsData?.past ?? [], [appointmentsData]);

  const upcoming = useMemo(
    () => upcomingList.filter((a) => a.status !== 'Cancelled' && !localCancellations.has(a.id)),
    [upcomingList, localCancellations],
  );
  const past = useMemo(
    () => [
      ...pastList,
      ...upcomingList.filter((a) => a.status === 'Cancelled' || localCancellations.has(a.id)),
    ],
    [pastList, upcomingList, localCancellations],
  );

  const openTasks = useMemo(
    () => tasks.filter((t) => t.status !== 'completed' && t.status !== 'cancelled'),
    [tasks],
  );

  return (
    <ClientPortalLayout
      title="Dashboard"
      description="Your appointments, action items, and assessments at a glance."
    >
      <Stack gap="xl">
        <Grid>
          {/* ── Upcoming appointment card ──────────────────────────────── */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper
              p="lg"
              withBorder
              style={{
                height: DASHBOARD_PANEL_HEIGHT,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <Flex
                justify="space-between"
                align="flex-start"
                gap="sm"
                mb="md"
                wrap="wrap"
                style={{ flexShrink: 0 }}
              >
                <Group gap="xs" wrap="nowrap">
                  <IconCalendarEvent size={18} stroke={1.5} />
                  <Title order={3}>Appointments</Title>
                </Group>
              </Flex>

              <ScrollArea flex={1} type="auto" offsetScrollbars style={{ minHeight: 0 }}>
                {appointmentsLoading ? (
                  <Loading />
                ) : (
                  <Tabs defaultValue="upcoming" keepMounted={false}>
                    <Tabs.List grow>
                      <Tabs.Tab value="upcoming">Upcoming</Tabs.Tab>
                      <Tabs.Tab value="past">Past</Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="upcoming" pt="md">
                      {upcoming.length === 0 ? (
                        <Text size="sm" c="gray.6">
                          No upcoming appointments.
                        </Text>
                      ) : (
                        <Stack gap="sm">
                          {upcoming.map((apt) => {
                            const statusBadge = upcomingAppointmentBadge(apt);
                            return (
                            <Card key={apt.id} withBorder padding="md" radius="md">
                              <Stack gap="sm">
                                <Flex
                                  direction={{ base: 'column', sm: 'row' }}
                                  justify="space-between"
                                  align={{ base: 'stretch', sm: 'flex-start' }}
                                  gap="xs"
                                >
                                  <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                                    <Text size="sm" fw={600} lineClamp={2}>
                                      {apt.serviceType}
                                    </Text>
                                    <Text size="sm" c="gray.7">
                                      {formatDate(apt.start)} · {formatTimeRange(apt.start, apt.end)}
                                    </Text>
                                    <Group gap={6}>
                                      <Badge size="xs" variant="dot" color="gray">
                                        {apt.modality}
                                      </Badge>
                                      <Text size="xs" c="dimmed">
                                        {apt.providerLabel}
                                      </Text>
                                    </Group>
                                  </Stack>
                                  <Badge
                                    variant="light"
                                    color={statusBadge.color}
                                    size="sm"
                                    style={{ flexShrink: 0, alignSelf: 'flex-start' }}
                                  >
                                    {statusBadge.label}
                                  </Badge>
                                </Flex>

                                <Group gap="xs" wrap="wrap" align="center">
                                  {isTelehealthVisit(apt) ? (
                                    <JoinVideoSessionButton
                                      appointmentId={apt.id}
                                      size="sm"
                                      variant="light"
                                      disabled={
                                        telehealthSessionHasFinished(apt.start, apt.end) ||
                                        chimeJoinNotYetOpen(apt.start)
                                      }
                                      disabledReason={
                                        telehealthSessionHasFinished(apt.start, apt.end)
                                          ? 'This session has already finished'
                                          : chimeJoinNotYetOpen(apt.start)
                                            ? `Join opens ${CHIME_JOIN_OPENS_MINUTES} minutes before your appointment starts`
                                            : undefined
                                      }
                                    />
                                  ) : null}
                                </Group>
                              </Stack>
                            </Card>
                            );
                          })}
                        </Stack>
                      )}
                    </Tabs.Panel>

                    <Tabs.Panel value="past" pt="md">
                      {past.length === 0 ? (
                        <Text size="sm" c="gray.6">
                          No past appointments.
                        </Text>
                      ) : (
                        <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="sm">
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Date</Table.Th>
                              <Table.Th>Time</Table.Th>
                              <Table.Th>Visit</Table.Th>
                              <Table.Th>Location</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {past.map((apt) => (
                              <Table.Tr key={apt.id}>
                                <Table.Td>
                                  <Text size="sm">{formatDate(apt.start)}</Text>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm">{formatTimeRange(apt.start, apt.end)}</Text>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm" fw={600}>
                                    {apt.serviceType}
                                  </Text>
                                  <Text size="xs" c="dimmed">
                                    {apt.providerLabel}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm">{apt.locationLabel ?? apt.modality}</Text>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      )}
                    </Tabs.Panel>
                  </Tabs>
                )}
              </ScrollArea>
            </Paper>
          </Grid.Col>

          {/* ── To Do (Task-backed) ───────────────────────────────────── */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper
              p="lg"
              withBorder
              style={{
                height: DASHBOARD_PANEL_HEIGHT,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <Group justify="space-between" mb="md" style={{ flexShrink: 0 }}>
                <Group gap="xs">
                  <IconChecklist size={18} stroke={1.5} />
                  <Title order={3}>To do</Title>
                </Group>
                {!tasksLoading && (
                  <Badge variant="light" color={openTasks.length > 0 ? 'blue' : 'green'}>
                    {openTasks.length} open
                  </Badge>
                )}
              </Group>

              <ScrollArea flex={1} type="auto" offsetScrollbars style={{ minHeight: 0 }}>
                {tasksLoading ? (
                  <Group justify="center" py="md"><Loader size="sm" /></Group>
                ) : tasksError ? (
                  <Alert variant="light" color="red" title="Could not load tasks">
                    <Text size="sm">{tasksError}</Text>
                  </Alert>
                ) : openTasks.length === 0 ? (
                  <Text size="sm" c="gray.6">
                    You&apos;re all caught up &mdash; no open tasks right now.
                  </Text>
                ) : (
                  <Stack gap="xs">
                    {openTasks.map((item) => {
                      const badge = taskBadgeLabel(item);
                      return (
                        <Card
                          key={item.id}
                          withBorder
                          padding="sm"
                          radius="md"
                          style={{ cursor: item.showActionButton ? 'pointer' : 'default' }}
                          onClick={
                            item.showActionButton
                              ? () => navigate(item.href)
                              : undefined
                          }
                        >
                          <Flex
                            direction={{ base: 'column', sm: 'row' }}
                            justify="space-between"
                            align={{ base: 'stretch', sm: 'flex-start' }}
                            gap="xs"
                          >
                            <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                              <Text size="sm" fw={600} lineClamp={2}>
                                {item.title}
                              </Text>
                              {item.description && (
                                <Text size="xs" c="gray.6" lineClamp={2}>
                                  {item.description}
                                </Text>
                              )}
                              {item.due && (
                                <Text size="xs" c={hoursUntil(item.due) < 48 ? 'red.6' : 'gray.6'}>
                                  Due {formatDate(item.due)}
                                </Text>
                              )}
                            </Stack>
                            {(badge || item.showActionButton) && (
                              <Group gap="xs" wrap="wrap" style={{ flexShrink: 0, alignSelf: 'flex-start' }}>
                                {badge && (
                                  <Badge size="sm" variant="light" color={taskBadgeColor(item)}>
                                    {badge}
                                  </Badge>
                                )}
                                {item.showActionButton ? (
                                  <Button
                                    size="xs"
                                    variant="light"
                                    rightSection={<IconExternalLink size={12} />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(item.href);
                                    }}
                                  >
                                    {item.actionLabel}
                                  </Button>
                                ) : null}
                              </Group>
                            )}
                          </Flex>
                        </Card>
                      );
                    })}
                  </Stack>
                )}
              </ScrollArea>
            </Paper>
          </Grid.Col>
        </Grid>
      </Stack>

      {/* ── Cancel appointment modal ───────────────────────────────────────── */}
      <Modal
        opened={!!cancelTarget}
        onClose={() => {
          if (cancelSubmitting) return;
          setCancelTarget(null);
        }}
        title="Cancel appointment"
        size="md"
        centered
        closeOnClickOutside={!cancelSubmitting}
        closeOnEscape={!cancelSubmitting}
      >
        {cancelTarget && (
          <Stack gap="md">
            <Paper p="md" withBorder radius="md">
              <Group justify="space-between" align="flex-start">
                <div>
                  <Text size="sm" fw={600}>{cancelTarget.serviceType}</Text>
                  <Text size="xs" c="gray.6" mt={4}>
                    {formatDate(cancelTarget.start)} &bull; {formatTimeRange(cancelTarget.start, cancelTarget.end)}
                  </Text>
                  <Text size="xs" c="gray.6">{cancelTarget.providerLabel}</Text>
                </div>
                <Badge variant="light" color="blue" size="sm">{cancelTarget.modality}</Badge>
              </Group>
            </Paper>

            <Text size="sm" c="gray.6">
              Are you sure you want to cancel this appointment for everyone? This action cannot be undone.
            </Text>

            <Group justify="flex-end" gap="sm" mt="xs">
              <Button
                variant="default"
                disabled={cancelSubmitting}
                onClick={() => {
                  setCancelTarget(null);
                }}
              >
                Keep appointment
              </Button>
              <Button
                color="red"
                loading={cancelSubmitting}
                disabled={!portalPatientId}
                onClick={() => {
                  void (async () => {
                    setCancelSubmitting(true);
                    try {
                      await submitPortalAppointmentCancellation(medplum, {
                        appointmentId: cancelTarget.id,
                      });
                      setLocalCancellations((prev) => new Set(prev).add(cancelTarget.id));
                      setCancelTarget(null);
                      notify({
                        type: 'success',
                        title: 'Appointment cancelled',
                        message: 'Your appointment was cancelled successfully.',
                      });
                    } catch (err) {
                      notify({
                        type: 'error',
                        title: 'Cancellation failed',
                        error: err,
                        message: 'Unable to cancel appointment.',
                      });
                    } finally {
                      setCancelSubmitting(false);
                    }
                  })();
                }}
              >
                Cancel appointment
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </ClientPortalLayout>
  );
}
